import { useCallback, useContext, useEffect, useState } from 'react';
import Script from 'next/script';
import { verifyGoogleIdToken, createGoogleUserSession } from './verifyToken';
import CookieService from '../../utils/miscUtils/CookieService';
import type { GoogleOneTapCredentialResponse, GoogleAuthResult } from './types';
import { trackEvent } from 'services/Gtm/functions';
import { useOptimizely, trackOptimizelyEvent } from 'providers/OptimizelyProvider';
import type { PageType } from 'interfaces/content/articles/Post';
import useBreakpoint from 'hooks/useBreakpoint';
import { UserPaywallContext } from '../../providers/PaywallProvider';
import { userTokenCookieDomain } from '@/app_lib/utils/clientCookieUtils';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: () => void;
          cancel: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface GoogleOneTapProps {
  clientId: string;
  onSuccess?: (userData: GoogleAuthResult) => void;
  onError?: (error: string) => void;
  pageType: PageType;
}

const GoogleOneTap: React.FC<GoogleOneTapProps> = ({ clientId, onSuccess, onError, pageType }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMobile = useBreakpoint('mdMax');
  const isTablet = useBreakpoint('lgMax');

  const { userFlags: userVariants } = useOptimizely();
  const { paywallCheck } = useContext(UserPaywallContext).paywallState;
  var variantToUse = 'off';
  const desktopVariants = userVariants?.googleOneTap as { variant: string };
  const mobileVariants = userVariants?.['googleOneTap-mobile'] as { variant: string };

  if (isMobile) {
    variantToUse = mobileVariants?.variant || 'off';
  } else {
    variantToUse = desktopVariants?.variant || 'off';
  }
  const oneTapEnabled = variantToUse !== 'off';

  let deviceType;
  if (isMobile) {
    deviceType = 'mobile';
  } else if (isTablet) {
    deviceType = 'tablet';
  } else {
    deviceType = 'desktop';
  }

  useEffect(() => {
    // Check if user is already logged in by checking Piano session cookies
    const utpCookie = CookieService.get('__utp');
    setIsLoggedIn(!!utpCookie);
  }, []);

  const handleAuthError = useCallback(
    (error: string, errorType: string) => {
      trackEvent({
        eventLabel: 'google_one_tap_login_error',
        eventAction: 'error/drop-off',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
          error: errorType,
        },
      });
      onError?.(error);
    },
    [pageType, deviceType, onError],
  );

  const handleSuccessfulAuth = useCallback(
    async (sessionResult: any) => {
      // Set Piano session cookies
      if (sessionResult.pianoToken && sessionResult.pianoUid) {
        const { domain } = userTokenCookieDomain();
        CookieService.set('__utp', sessionResult.pianoToken, {
          secure: true,
          sameSite: 'strict',
          ...(domain && { domain }),
        });
      }

      // Immediately update login state to prevent duplicate prompts
      setIsLoggedIn(true);

      // Create success data
      const successData: GoogleAuthResult = {
        success: true,
        pianoUid: sessionResult.pianoUid,
        accessToken: sessionResult.pianoToken,
        isNewUser: sessionResult.isNewUser,
        message: sessionResult.message,
      };

      // Track success events
      trackEvent({
        eventLabel: 'google_one_tap_login_success',
        eventAction: 'conversion',
        customData: {
          pageType,
          deviceType,
          isNewUser: JSON.stringify(sessionResult.isNewUser),
          user_registration_type: 'google_one_tap',
        },
      });
      trackOptimizelyEvent('user_signed_in', {});

      if (sessionResult.isNewUser) {
        trackEvent({
          eventLabel: 'google_one_tap_user_created',
          eventAction: 'conversion',
          customData: {
            pageType,
            deviceType,
            isNewUser: JSON.stringify(sessionResult.isNewUser),
            user_registration_type: 'google_one_tap',
          },
        });
        trackOptimizelyEvent('user_signed_up', {});
      }

      onSuccess?.(successData);

      // Reload page after slight delay
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [pageType, deviceType, onSuccess],
  );

  const processAuthentication = useCallback(
    async (credential: string) => {
      // Verify Google ID token
      const verificationResult = await verifyGoogleIdToken(credential);

      if (!verificationResult.success) {
        handleAuthError(verificationResult.error || 'Token verification failed', 'token_verification_failed');
        return;
      }

      trackEvent({
        eventLabel: 'google_one_tap_credential_received',
        eventAction: 'click/engagement',
        customData: { pageType, deviceType, user_registration_type: 'google_one_tap' },
      });
      trackOptimizelyEvent('google_one_tap_continue', {});

      if (!verificationResult.payload) {
        handleAuthError('Invalid token payload', 'invalid_token_payload');
        return;
      }

      // Create user session
      const sessionResult = await createGoogleUserSession(verificationResult.payload);

      if (!sessionResult.success) {
        handleAuthError(sessionResult.error || 'Session creation failed', 'session_creation_failed');
        return;
      }

      await handleSuccessfulAuth(sessionResult);
    },
    [handleAuthError, handleSuccessfulAuth, pageType, deviceType],
  );

  const handleCredentialResponse = useCallback(
    async (response: GoogleOneTapCredentialResponse) => {
      if (isProcessing || isLoggedIn) return;

      setIsProcessing(true);

      try {
        await processAuthentication(response.credential);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to process Google One Tap response';
        onError?.(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing, isLoggedIn, processAuthentication, onError],
  );

  function momentCallback(notification) {
    if (notification.getNotDisplayedReason?.() === 'suppressed_by_user') {
      trackOptimizelyEvent('google_one_tap_dismissed', {});
      trackEvent({
        eventLabel: 'google_one_tap_prompt_dismissed',
        eventAction: 'click/engagement',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }

    if (notification.isDisplayed?.()) {
      trackOptimizelyEvent('google_one_tap_shown', {});
      trackEvent({
        eventLabel: 'google_one_tap_prompt_shown',
        eventAction: 'impression',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }

    if (notification.getSkippedReason?.() === 'tap_outside') {
      trackOptimizelyEvent('google_one_tap_skipped', {});
      trackEvent({
        eventLabel: 'google_one_tap_prompt_dismissed',
        eventAction: 'click outside/engagement',
        customData: {
          pageType,
          deviceType,
          user_registration_type: 'google_one_tap',
        },
      });
    }
  }

  useEffect(() => {
    // Don't initialize if paywall is active, pending, or user is logged in, or processing
    if (paywallCheck === 'paywalled' || paywallCheck === 'pending' || isLoggedIn || isProcessing) {
      return;
    }

    // Initialize Google One Tap when the script is loaded
    const initializeGoogleOneTap = () => {
      if (window.google?.accounts?.id) {
        const config: any = {
          client_id: clientId,
          context: 'signin',
          cancel_on_tap_outside: true,
          auto_select: false,
        };

        config.callback = handleCredentialResponse;
        config.moment_callback = momentCallback;

        window.google.accounts.id.initialize(config);
        (window.google.accounts.id.prompt as any)(momentCallback);
      }
    };

    // Check if Google accounts is already available
    if (window.google?.accounts?.id) {
      initializeGoogleOneTap();
    } else {
      // Wait for the script to load
      const checkGoogleLoaded = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogleLoaded);
          initializeGoogleOneTap();
        }
      }, 100);

      return () => clearInterval(checkGoogleLoaded);
    }
  }, [clientId, handleCredentialResponse, paywallCheck, isLoggedIn, isProcessing]);

  // Don't render Google One Tap if user is already logged in or login is being processed
  if (isLoggedIn === null) {
    // Still checking login status, don't render anything yet
    return null;
  }

  if (isLoggedIn || isProcessing) {
    // User is already logged in or login is being processed - don't show Google One Tap
    return null;
  }

  return (
    oneTapEnabled && (
      <Script
        src='https://accounts.google.com/gsi/client'
        strategy='afterInteractive'
      />
    )
  );
};

export default GoogleOneTap;
